/**
 * Waitlist demand — Stage 4.
 *
 * Sources `/api/bi/waitlist/demand/` (per-address aggregate of service-area
 * requests). The map is a deliberately tiny SVG hexbin — full MapLibre
 * lands later in Stage 7's network deep-dive.
 */
import { useMemo, useState } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { MapPin, Sparkles } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/ui/ErrorState';
import { EmptyState } from '@/components/ui/EmptyState';
import { DataView } from '@/components/data/DataView';
import { Sparkline } from '@/components/charts/Sparkline';
import { KPICard } from '@/components/charts/KPICard';
import { useWaitlistDemand, type WaitlistDemandRow } from '@/api/bi';
import { rangeISO, useFilters } from '@/state/filters';
import { fmt } from '@/lib/format';

interface PointPreviewProps {
  rows: WaitlistDemandRow[];
}

function PointPreview({ rows }: PointPreviewProps) {
  const points = rows.filter((r) => r.latitude != null && r.longitude != null);
  if (points.length === 0) {
    return (
      <div className="grid h-48 place-items-center rounded-xl border border-dashed border-border/60 text-xs text-muted-foreground">
        No coordinates in this window.
      </div>
    );
  }
  const lats = points.map((p) => p.latitude as number);
  const lngs = points.map((p) => p.longitude as number);
  const latMin = Math.min(...lats);
  const latMax = Math.max(...lats);
  const lngMin = Math.min(...lngs);
  const lngMax = Math.max(...lngs);
  const dLat = Math.max(0.0005, latMax - latMin);
  const dLng = Math.max(0.0005, lngMax - lngMin);
  const max = Math.max(...points.map((p) => p.count));
  return (
    <div className="relative h-48 overflow-hidden rounded-xl border border-border/60 bg-[radial-gradient(circle_at_50%_50%,hsl(var(--primary)/0.08),transparent_70%)]">
      <svg viewBox="0 0 100 60" className="h-full w-full">
        {points.map((p, i) => {
          const x = ((p.longitude as number) - lngMin) / dLng * 96 + 2;
          const y = 58 - ((p.latitude as number) - latMin) / dLat * 56;
          const r = 0.6 + (p.count / max) * 2.2;
          return (
            <g key={`${p.latitude}-${p.longitude}-${i}`}>
              <circle cx={x} cy={y} r={r * 1.6} fill="hsl(var(--primary))" opacity={0.18} />
              <circle cx={x} cy={y} r={r} fill="hsl(var(--primary))" />
            </g>
          );
        })}
      </svg>
      <div className="absolute bottom-1.5 right-2 rounded-full border border-border bg-surface/90 px-1.5 py-0.5 text-[10px] text-muted-foreground">
        {points.length} addresses
      </div>
    </div>
  );
}

export function WaitlistPage() {
  const range = useFilters((s) => s.range);
  const timeRangeMode = useFilters((s) => s.timeRangeMode);
  const [search, setSearch] = useState('');
  const { start: startISO, end: endISO } = useMemo(
    () => rangeISO(range, timeRangeMode),
    [range, timeRangeMode],
  );
  const query = useWaitlistDemand({ start: startISO, end: endISO, limit: 500 });

  const all = query.data?.rows ?? [];
  const filtered = useMemo(() => {
    if (!search.trim()) return all;
    const q = search.trim().toLowerCase();
    return all.filter((r) => (r.address ?? '').toLowerCase().includes(q));
  }, [all, search]);

  const trendSeries = (query.data?.trend ?? []).map((b) => ({ value: b.count }));

  const columns = useMemo<ColumnDef<WaitlistDemandRow, unknown>[]>(
    () => [
      {
        id: 'address',
        header: 'Address',
        cell: ({ row }) => (
          <div className="flex items-start gap-2">
            <MapPin className="mt-0.5 h-3.5 w-3.5 text-muted-foreground" aria-hidden />
            <div className="min-w-0">
              <div className="truncate text-sm">{row.original.address || '—'}</div>
              {row.original.latitude != null && row.original.longitude != null ? (
                <div className="text-[10px] font-mono text-muted-foreground">
                  {row.original.latitude.toFixed(4)}, {row.original.longitude.toFixed(4)}
                </div>
              ) : null}
            </div>
          </div>
        ),
      },
      {
        id: 'count',
        header: () => <div className="text-right">Requests</div>,
        cell: ({ row }) => (
          <div className="kpi-num text-right text-sm font-medium">
            {fmt.number(row.original.count)}
          </div>
        ),
      },
      {
        id: 'people',
        header: () => <div className="text-right">People</div>,
        cell: ({ row }) => (
          <div className="kpi-num text-right text-sm">{fmt.number(row.original.unique_users)}</div>
        ),
      },
      {
        id: 'last',
        header: 'Last seen',
        cell: ({ row }) =>
          row.original.last_seen ? (
            <span className="text-xs text-muted-foreground">
              {format(new Date(row.original.last_seen), 'MMM d, HH:mm')}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground/60">—</span>
          ),
      },
    ],
    [],
  );

  const totals = query.data?.totals;

  return (
    <>
      <PageHeader
        eyebrow="Demand"
        title="Waitlist demand"
        description="People who asked us to come deliver to them. Treat this as the pipeline for store + zone expansion."
        actions={
          <Badge tone="info" className="gap-1">
            <Sparkles className="h-3 w-3" /> Stage 4
          </Badge>
        }
      />

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPICard
          label="Total requests"
          value={fmt.number(totals?.requests ?? 0)}
          loading={query.isLoading}
          trend={trendSeries}
          hint="Service-area requests in window."
        />
        <KPICard
          label="Unique addresses"
          value={fmt.number(totals?.unique_addresses ?? 0)}
          loading={query.isLoading}
          hint="Distinct lat/lng/address combinations."
        />
        <KPICard
          label="Hopeful neighbours"
          value={fmt.number(totals?.unique_users ?? 0)}
          loading={query.isLoading}
          hint="Distinct users who raised at least one request."
        />
        <Card className="overflow-hidden">
          <CardHeader>
            <CardTitle>Where to look next</CardTitle>
          </CardHeader>
          <CardBody className="text-xs text-muted-foreground">
            Cluster the map by eye — the densest spots away from existing geofences are your best
            candidates for a new dark-store or rider beat.
          </CardBody>
        </Card>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>Request map</CardTitle>
            <Badge tone="neutral">{filtered.length} pin{filtered.length === 1 ? '' : 's'}</Badge>
          </CardHeader>
          <CardBody>
            {query.isLoading ? (
              <Skeleton className="h-48 w-full rounded-xl" />
            ) : filtered.length === 0 ? (
              <EmptyState title="No requests yet" description="Pick a wider window." />
            ) : (
              <PointPreview rows={filtered} />
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Daily trend</CardTitle>
          </CardHeader>
          <CardBody>
            {query.isLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : trendSeries.length === 0 ? (
              <EmptyState title="No data" />
            ) : (
              <div>
                <Sparkline data={trendSeries} tone="primary" />
                <div className="mt-2 text-[11px] text-muted-foreground">
                  {(query.data?.trend ?? []).length} day buckets · last spike{' '}
                  {(query.data?.trend ?? []).length > 0
                    ? format(new Date((query.data?.trend ?? [])[(query.data?.trend ?? []).length - 1].day), 'MMM d')
                    : '—'}
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      <div className="mt-4">
        {query.isError ? (
          <ErrorState description="Couldn't load waitlist demand." onRetry={() => void query.refetch()} />
        ) : (
          <DataView<WaitlistDemandRow>
            title="Address rollups"
            data={filtered}
            columns={columns}
            isLoading={query.isLoading}
            search={{ value: search, onChange: setSearch, placeholder: 'Search by address…' }}
            emptyTitle="No requests match"
          />
        )}
      </div>
    </>
  );
}
